import { ChartData } from "components/dashboard/dataset-window/models/types";
import { DatasetLoadingConf, IDataSet } from "components/dashboard/charts/interfaces";
import { addToDate, ChronoUnitLegacy, formatTime, truncateDate } from "utils/time-utils";

export const colorPallet = {
  key: "Category10",
  value: [
    "#1f77b4",
    "#ff7f0e",
    "#2ca02c",
    "#d62728",
    "#9467bd",
    "#8c564b",
    "#e377c2",
    "#7f7f7f",
    "#bcbd22",
    "#17becf",
    "#393b79",
    "#637939",
    "#8c6d31",
    "#843c39",
    "#7b4173",
    "#a55194",
    "#ce6dbd",
    "#de9ed6",
    "#393b79",
    "#5254a3",
    "#6b6ecf",
    "#9c9ede",
    "#637939",
    "#8ca252",
  ],
};

export const offsetOptions = [
  { label: "H", value: "HOURS" },
  { label: "D", value: "DAYS" },
  { label: "W", value: "WEEKS" },
  { label: "M", value: "MONTHS" },
  { label: "Y", value: "YEARS" },
];

export const hoursArray = Array.from({ length: 24 }, (_, i) => {
  const start = `${i.toString().padStart(2, "0")}:00`;
  const end = `${((i + 1) % 24).toString().padStart(2, "0")}:00`;
  return `${start} - ${end}`;
});

export const daysArray = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];
export const weeksArray = ["Week 1", "Week 2", "Week 3", "Week 4"];
export const monthsArray = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
export const yearsArray = Array.from({ length: 24 }, (_, i) => (2017 + i).toString());

export const initialColorAxisDataset = { data: [], identifier: "", title: "", unit: "" };
export const options = [
  { name: "Range", value: true },
  { name: "Discrete", value: false },
];
export const colorAxisOptions = [
  { name: "Dataset", value: true },
  { name: "Time Offset", value: false },
];

export function defaultFromDateTime(props: {
  loadConf?: DatasetLoadingConf;
  referenceTime?: string;
}) {
  const align = props.loadConf?.alignUnit;
  const lag = props.loadConf?.lag ?? 6;
  const lagUnit = props.loadConf?.lagUnit ?? "HOURS";
  const startingDate = props.referenceTime ? new Date(props.referenceTime) : new Date();
  const date = truncateDate(startingDate, align);

  return addToDate(date, -lag, lagUnit);
}

export function defaultToDateTime(props: {
  loadConf?: DatasetLoadingConf;
  referenceTime?: string;
}) {
  const align = props.loadConf?.alignUnit;
  const lead = props.loadConf?.lead ?? 0;
  const leadUnit = props.loadConf?.leadUnit ?? "HOURS";

  const startingDate = props.referenceTime ? new Date(props.referenceTime) : new Date();

  const date = truncateDate(startingDate, align);

  return addToDate(addToDate(date, lead, leadUnit), 1, "MINUTES");
}

export const convertStringDateToDate = (dataArray: Array<any>) => {
  return dataArray.map(d => {
    return new Date(d.key);
  });
};

export const convertValue = (dataArray: Array<any>) => {
  return dataArray?.map(d => {
    return d.value;
  });
};

export const convertKey = (dataArray: Array<any>) => {
  return dataArray.map(d => {
    return new Date(d.key);
  });
};

const getValuesToDisplay = (
  dates: Date[],
  timeOffset: string | undefined,
  colorPallet: string[]
) => {
  if (!timeOffset) {
    return [];
  }

  const uniqueValues = new Set();

  let values = [];

  switch (timeOffset) {
    case ChronoUnitLegacy.HOURS:
      values = dates.map(date => {
        const hour = date.getHours();
        const start = `${hour.toString().padStart(2, "0")}:00`;
        const end = `${((hour + 1) % 24).toString().padStart(2, "0")}:00`;
        return `${start} - ${end}`;
      });
      break;
    case ChronoUnitLegacy.DAYS:
      values = dates.map(date => date.toLocaleString("en-US", { weekday: "long" }));
      break;
    case ChronoUnitLegacy.WEEKS:
      values = dates.map(date => {
        const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
        const weekNumber = Math.ceil((date.getDate() + startOfMonth.getDay()) / 7);
        return `Week ${weekNumber}`;
      });
      break;
    case ChronoUnitLegacy.MONTHS:
      values = dates.map(date => date.toLocaleString("en-US", { month: "long" }));
      break;
    case ChronoUnitLegacy.YEARS:
      values = dates.map(date => date.getFullYear().toString());
      break;
    default:
      return [];
  }

  values.forEach(value => uniqueValues.add(value));

  const valueToColor = Array.from(uniqueValues).reduce((acc, value, index) => {
    acc[value] = colorPallet[index % colorPallet.length];
    return acc;
  }, {});

  return values.map(value => ({
    value,
    color: valueToColor[value],
  }));
};

export const convertDataToChartsData = (
  datasetArray: Array<IDataSet>,
  scatterSelectedTimeOffset?: string
): ChartData[] => {
  return datasetArray.map(d => {
    const keyArr = convertKey(d.data);
    const valueArr = convertValue(d.data);

    if (scatterSelectedTimeOffset) {
      const valuesToDisplay = getValuesToDisplay(
        keyArr,
        scatterSelectedTimeOffset,
        colorPallet.value
      );

      return {
        id: d.identifier,
        title: d.title.replace(/_/g, " "),
        unit: d.unit,
        stringValueMapper: d.stringValueMapper,
        keyArr,
        valueArr,
        decimalPlaces: d.decimalPlaces,
        scatterSelectedTimeOffset,
        valuesToDisplay,
      } as ChartData;
    }
    return {
      id: d.identifier,
      title: d.title.replace(/_/g, " "),
      unit: d.unit,
      stringValueMapper: d.stringValueMapper,
      keyArr,
      valueArr,
      decimalPlaces: d.decimalPlaces,
    } as ChartData;
  });
};

export const isElementInDom = (id: string) => {
  const elementExists = document.getElementById(id);
  return !!elementExists;
};

export const round2decimals = (num: number) => {
  return Math.round((num + Number.EPSILON) * 100) / 100;
};

export const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  let colorRGB = null;
  if (result !== null) {
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    colorRGB = `rgba(${r},${g},${b},0.7)`;
  }
  return colorRGB || null;
};

interface ITableRowData {
  date: Date;

  [key: string]: string | Date | null;
}

export const getAllDatasetsTimestamps = (
  datasets: Array<IDataSet>
): {
  content: Array<{ timestamp: string; date: Date; [identifier: string]: string | Date | null }>;
  columns: Array<{ identifier: string, title: string }>;
} => {
  const tableTemplate: { [key: string]: ITableRowData } = {};
  const columns = datasets.map(dataset => ({
    identifier: dataset.identifier,
    title: dataset.title.replace(/_/g, " "),
  }));

  datasets.forEach(dataset => {
    dataset.data.forEach(({ key: dateStr, value }) => {
      const date = new Date(dateStr);
      const tempKey = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()} ${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}`;

      if (!tableTemplate[tempKey]) {
        tableTemplate[tempKey] = { date };
      }

      tableTemplate[tempKey][dataset.identifier] =
        value != null ? value.toFixed(dataset.decimalPlaces ?? 2) : null;
    });
  });

  const tableTemplateArray = Object.entries(tableTemplate)
    .map(([key, value]) => ({
      timestamp: key,
      ...value,
    }))
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  return {
    columns,
    content: tableTemplateArray.map(obj => ({ ...obj, timestamp: formatTime(obj.timestamp) })),
  };
};

export const splitLongWord = (word: string) => {
  const chunks = [];
  for (let i = 0; i < word.length; i += 15) {
    chunks.push(word.substring(i, i + 15));
  }
  return chunks.join("-<br>");
};

export const splitStringToLines = (str: string) => {
  const words = str.split(" ");
  const lines = [];
  let currentLine = "";
  for (const word of words) {
    if (currentLine.length + word.length + 1 <= 15) {
      currentLine += (currentLine ? " " : "") + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  if (currentLine) {
    lines.push(currentLine);
  }
  return lines;
};

export const shouldShowTime = (cfg?: any): boolean => {
  const unitsToCheck = [
    cfg?.alignUnit,
    cfg?.lagUnit,
    cfg?.leadUnit,
    cfg?.alignStartTo,
    cfg?.startOffsetUnits,
    cfg?.lagUnits,
    cfg?.leadUnits,
    cfg?.stepUnits,
  ];

  return unitsToCheck.some(
    unit =>
      unit === ChronoUnitLegacy.SECONDS ||
      unit === ChronoUnitLegacy.MINUTES ||
      unit === ChronoUnitLegacy.HOURS
  );
};